import React, { FC, memo, useCallback, useMemo, useEffect } from 'react';
import { useFormikContext } from 'formik';
import { useLazyQuery } from '@apollo/client';
import _find from 'lodash/find';
import { setFieldValueAndTouched } from '@minecraft.utils';
import { GetHeightsDoc } from '@minecraft.graphql-operations';
import ErrorMessage from '@minecraft.error-message';
import { useTranslation } from '@blocs.i18n';
import { emptyOption } from '../../../shared/config';
import { UnitSystemNameEnum, RangeEnum, FilterData } from '../../../shared/types';
import { generateSelectOptionsBasedOnField, useMeasure } from '../../../shared/utils';
import { Label, StyledCheckbox } from '../../../shared/styles';
import { RangeSelector } from '../../shared/components';

type Props = {
  heightRangeName: FilterData['name'];
  switchHeightToMetricName: FilterData['name'];
  heightMinName: FilterData['name'];
  heightMaxName: FilterData['name'];
  label: FilterData['label'];
};

const HeightRange: FC<Props> = ({
  heightRangeName,
  switchHeightToMetricName,
  heightMinName,
  heightMaxName,
  label: primaryLabel,
}) => {
  const { t } = useTranslation();
  const formikContext = useFormikContext();

  const switchToMetric = formikContext.values[heightRangeName][switchHeightToMetricName];
  const minValue = formikContext.values[heightRangeName][heightMinName];
  const maxValue = formikContext.values[heightRangeName][heightMaxName];

  const [getHeights, { data, error }] = useLazyQuery(GetHeightsDoc);

  const handleSwitchToMetricChange = useCallback((): void => {
    const updatedSwitchToMetric = !switchToMetric;
    const updatedHeightOptions = generateSelectOptionsBasedOnField(
      data?.heights,
      updatedSwitchToMetric ? UnitSystemNameEnum.centimeters : UnitSystemNameEnum.feetAndInches
    );

    setFieldValueAndTouched(`${heightRangeName}.${switchHeightToMetricName}`, updatedSwitchToMetric, formikContext);

    if (minValue.value) {
      const updatedMinValue = _find(updatedHeightOptions, { value: minValue.value }) || emptyOption;

      formikContext.setFieldValue(`${heightRangeName}.${heightMinName}`, updatedMinValue);
    }

    if (maxValue.value) {
      const updatedMaxValue = _find(updatedHeightOptions, { value: maxValue.value }) || emptyOption;

      formikContext.setFieldValue(`${heightRangeName}.${heightMaxName}`, updatedMaxValue);
    }
  }, [switchToMetric, minValue, maxValue, data]);

  useEffect(() => {
    if (!data) {
      getHeights();
    }
  }, [data]);

  const heightOptions = useMemo(() => {
    return generateSelectOptionsBasedOnField(
      data?.heights,
      switchToMetric ? UnitSystemNameEnum.centimeters : UnitSystemNameEnum.feetAndInches
    );
  }, [switchToMetric, data]);

  const label = `${primaryLabel} (${useMeasure({
    switchTo: switchToMetric,
    type: RangeEnum.height,
  })})`;

  if (error) return <ErrorMessage error={error} />;

  return (
    <>
      <Label>{label}</Label>
      <RangeSelector
        saveAsOption
        showInImperial={!switchToMetric}
        rangeName={heightRangeName}
        minName={heightMinName}
        maxName={heightMaxName}
        options={heightOptions}
      />
      <StyledCheckbox
        isChecked={switchToMetric}
        name={`${heightRangeName}.${switchHeightToMetricName}`}
        label={t('common:form.title.switchToMetric')}
        onChange={handleSwitchToMetricChange}
      />
    </>
  );
};

export default memo(HeightRange);
